import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@knocklabs/react';

import { useNewFilters } from 'hooks/useFilters';
import { createHandle } from 'services/breadcrumbs/breadcrumbs';
import { useInfiniteInboxMessages } from 'services/repositories/inbox/inbox';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { translationForDocumentTypeWithVirtual } from 'support/helpers/transactions/transactions';
import { DashboardRoutes, InboxRoutes, MessageDTO, TransactionInternalType } from 'support/types';
import { PropertyFilter, PropertyFilterType } from 'support/types/filters';
import { InfiniteScroll } from 'components/ListTable/InfiniteScroll/InfiniteScroll';
import { ListTable } from 'components/ListTable/ListTable';
import { TableHeader } from 'components/ListTable/types';
import { Page } from 'components/Page/Page';
import { TransactionStatusDisplay } from 'components/TransactionStatusDisplay/TransactionStatusDisplay';

const supportedTypes = [
  'pendingOrders',
  'pendingOrderResponses',
  'pendingShippingNotices',
  'pendingInvoices',
  'pendingDispatchInstructions',
];
const filters: Array<PropertyFilter> = [
  {
    key: 'type',
    type: PropertyFilterType.search,
    value: supportedTypes[0],
  },
];

export const Inbox = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: currentPartner } = useCurrentPartner();
  const { filterValues, perPage } = useNewFilters(filters, 'inbox');
  const { track } = useAnalytics();
  const activeType = filterValues.find((filter) => filter.key === 'type')?.value;

  const {
    data: messagesData,
    isLoading: inboxMessagesLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasPreviousPage,
    hasNextPage,
  } = useInfiniteInboxMessages({
    variables: {
      query: {
        filterValues,
        perPage,
      },
    },
    staleTime: 0, //make sure to override our default options
    refetchOnMount: true, //make sure to override our default options
  });

  useEffect(() => {
    if (!activeType || !supportedTypes.includes(activeType)) {
      navigate(DashboardRoutes.root);
    }
  }, [activeType, navigate]);

  const tableHeaders: Array<TableHeader> = [
    { label: t('transactions:list.tableHeaders.externalReference') },
    { label: t('transactions:list.tableHeaders.type') },
    { label: t('transactions:list.tableHeaders.tradePartner') },
    { label: t('transactions:list.tableHeaders.status') },
    { label: t('transactions:list.tableHeaders.date') },
  ];

  const buildRow = (message: MessageDTO, index: number) => {
    const partner = currentPartner?.id !== message.sender?.id ? message.sender : message.receiver;
    const typeLocale = translationForDocumentTypeWithVirtual(t, message.type);

    return [
      {
        element: (
          <div className="text-sm text-gray-900">
            {message.externalMessageRef?.length ? message.externalMessageRef : EMPTY_FIELD}
          </div>
        ),
        key: `external-ref ${message.id ?? ''} ${index}`,
      },
      {
        element: <div className="text-sm text-gray-900">{message.type?.length ? typeLocale : EMPTY_FIELD}</div>,
        key: 'type',
      },
      {
        element: <div className="text-sm text-gray-900">{partner?.name ?? EMPTY_FIELD}</div>,
        key: 'partner-name',
      },
      {
        element: <TransactionStatusDisplay state={message.state} />,
        key: 'message-status',
      },
      {
        element: <div className="text-sm text-gray-900">{formatDayAndTime(message.createdAt)}</div>,
        key: 'created-at',
      },
    ];
  };

  return (
    <Page stickyMode="full">
      <Page.Head title={t(`inbox:overview.${activeType}`)} description={t('inbox:overview.description')} />
      <Page.Section>
        <ListTable<MessageDTO>
          headers={tableHeaders}
          data={messagesData?.pages?.flatMap((page) => page.items) ?? []}
          isLoading={inboxMessagesLoading}
          rowBuilder={buildRow}
          rowClickPath={(message) => ({
            url: routeToTransactionPage(TransactionInternalType.message, message.id),
            state: { keepBreadcrumbs: true },
          })}
          emptyState={
            <ListTable.EmptyState cols={tableHeaders.length}>
              {t(`inbox:overview.emptyState.${activeType}`)}
            </ListTable.EmptyState>
          }
          pagination={
            <InfiniteScroll
              hasMore={Boolean(hasNextPage)}
              loadMore={fetchNextPage}
              loading={isFetchingNextPage}
              noMoreItemsEnabled={Boolean(hasPreviousPage)}
            />
          }
        />

        <Modal
          onButtonClick={(_, guideButtonArgs) => {
            window.open(guideButtonArgs.button.action, '_blank');
            track('knock_guide:inbox_click');
          }}
        />
      </Page.Section>
    </Page>
  );
};

Inbox.handle = createHandle(() => {
  return {
    label: (t) => t('inbox:breadcrumbs'),
    to: InboxRoutes.root,
    key: InboxRoutes.root,
  };
});

/*
 * i18n
 * t('inbox:overview.pendingOrders')
 * t('inbox:overview.pendingOrderResponses')
 * t('inbox:overview.pendingShippingNotices')
 * t('inbox:overview.pendingInvoices')
 * t('inbox:overview.pendingDispatchInstructions')
 *
 * t('inbox:overview.emptyState.pendingOrders')
 * t('inbox:overview.emptyState.pendingOrderResponses')
 * t('inbox:overview.emptyState.pendingShippingNotices')
 * t('inbox:overview.emptyState.pendingInvoices')
 * t('inbox:overview.emptyState.pendingDispatchInstructions')
 */
